import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Camera, CheckCircle, Clock, XCircle, ArrowLeft, Download, Image } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const freeOrderId = searchParams.get('order_id');
  const isFreeOrder = searchParams.get('free') === 'true';
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const [orderId, setOrderId] = useState(null);
  const [downloads, setDownloads] = useState([]);

  useEffect(() => {
    if (isFreeOrder && freeOrderId) {
      // Free order - already paid, just fetch downloads
      setStatus('success');
      setOrderId(freeOrderId);
      fetchDownloadsForOrder(freeOrderId);
    } else if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, isFreeOrder, freeOrderId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Try to get order ID from payment and fetch downloads
        fetchDownloads();
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  const fetchDownloadsForOrder = async (ordId) => {
    try {
      const downloadsRes = await axios.get(`${API}/orders/${ordId}/downloads`);
      setDownloads(downloadsRes.data.downloads || []);
    } catch (error) {
      console.error('Error fetching downloads:', error);
    }
  };

  const fetchDownloads = async () => {
    try {
      // Get order by session ID
      const paymentRes = await axios.get(`${API}/payments/session/${sessionId}/order`);
      if (paymentRes.data?.order_id) {
        setOrderId(paymentRes.data.order_id);
        fetchDownloadsForOrder(paymentRes.data.order_id);
      }
    } catch (error) {
      console.error('Error fetching downloads:', error);
    }
  };

  const handleDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-200">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <Camera className="h-8 w-8 text-blue-600 mr-2" />
            <span className="text-2xl font-heading font-bold text-slate-900">LensLeague</span>
          </div>

          {status === 'checking' && (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-6" />
              <h1 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                Processing Payment
              </h1>
              <p className="text-slate-500">
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="bg-green-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                Payment Successful!
              </h1>
              <p className="text-slate-500 mb-6">
                Thank you for your order. You will receive a confirmation email shortly.
              </p>
              
              {/* Digital Downloads Section */}
              {downloads.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
                  <h2 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    Your Digital Downloads
                  </h2>
                  <div className="space-y-2">
                    {downloads.map((download, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-2 bg-white rounded border border-blue-100"
                      >
                        <div className="flex items-center gap-2">
                          <Image className="h-4 w-4 text-slate-500" />
                          <span className="text-sm text-slate-700">{download.product_name}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          onClick={() => handleDownload(download.photo_url, download.photo_filename)}
                          data-testid={`download-${index}`}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-blue-600 mt-3">
                    Download links are also included in your confirmation email.
                  </p>
                </div>
              )}
              
              <Link to="/">
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="back-home-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="bg-red-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <XCircle className="h-12 w-12 text-red-600" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                Payment Session Expired
              </h1>
              <p className="text-slate-500 mb-6">
                Your payment session has expired. Please try again.
              </p>
              <Link to="/">
                <Button variant="outline" data-testid="try-again-btn">
                  Try Again
                </Button>
              </Link>
            </>
          )}

          {status === 'timeout' && (
            <>
              <div className="bg-amber-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <Clock className="h-12 w-12 text-amber-600" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                Payment Pending
              </h1>
              <p className="text-slate-500 mb-6">
                We couldn't confirm your payment status. Please check your email for confirmation or contact support.
              </p>
              <Link to="/">
                <Button variant="outline" data-testid="back-home-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
